import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import cx from 'clsx';
import message from 'antd/es/message';

import { Modal, Button, Input, Textarea, Tag, HelperTooltip, Checkbox } from '@/components';
import { ProposalListItem } from '../../ListContainer';
import { useCreateProposal, useCheckProposalDuplicate, useCheckProposalSimilar } from '../../hooks';
import { PROPOSAL_TYPES } from './const';
import style from './index.module.scss';
import { WrapModalProps, errorToString, useCurrentUserId, wrapModalPromise } from '@/utils';
import { ProposalItem } from '../../types';
import { useFeedCreate } from '@/views/Stream';

const { CommonModal } = Modal;

export type CreatePropsalModalProps = {
  turnOff?: () => void;
  initType?: string;
  initAddrs?: string;
  initUrls?: string;
  initExtra?: string;
  initIssueAddr?: string;
  onUpdate?: () => void;
  // 对于某个二级类，在 description 字段中添加一段额外文本
  // Record<二级类，添加的额外文本>
  appendDescription?: Record<string, string | undefined>;

  initSubType?: string;
  initRule?: {
    anyof?: string[];
    required?: string[];
  };
};

export const CreatePropsalModal = ({
  turnOff,
  initType,
  initAddrs,
  initUrls,
  initExtra,
  initIssueAddr,
  onUpdate,
  appendDescription,
  hide,
  initSubType = '',
  initRule,
}: CreatePropsalModalProps & Partial<WrapModalProps>) => {
  const { t } = useTranslation(void 0, { lng: 'en' });

  const _requestClose = hide || turnOff;

  const history = useHistory();
  const [type, setType] = useState(initType || PROPOSAL_TYPES[0].label);
  const currentType = useMemo(() => PROPOSAL_TYPES.find(x => x.label === type), [type]);
  const subTypes = useMemo(() => currentType?.types, [currentType]);
  const [subType, setSubType] = useState(initSubType);
  const currentSubType = useMemo(
    () => subTypes?.find(x => x.label === subType),
    [subTypes, subType],
  );
  const userId = useCurrentUserId();

  const [duplicate, setDuplicate] = useState('');
  const [shareOnStream, setShareOnStream] = useState(true);
  const [forceCreateSimilarProposals, setForceCreateSimilarProposals] = useState(false);
  const [similarProposals, setSimilarProposals] = useState<ProposalItem[] | null>(null);
  const { mutateAsync: checkProposalDuplicate, isLoading: isCheckDuplicateLoading } =
    useCheckProposalDuplicate();
  const { mutateAsync: checkProposalSimilar, isLoading: isCheckSimilarLoading } =
    useCheckProposalSimilar();
  const { mutateAsync: createProposal, isLoading } = useCreateProposal();
  const { mutateAsync: createStream, isLoading: createStreamLoading } = useFeedCreate(true);

  const {
    handleSubmit,
    control,
    trigger,
    formState: { isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      addrs: initAddrs,
      urls: initUrls,
      extra: initExtra,
      issueAddr: initIssueAddr,
    },
  });

  const handleTypeClick = useCallback(
    ({ currentTarget }: React.MouseEvent<HTMLSpanElement>) => {
      const { label } = currentTarget.dataset;

      if (label) {
        setType(label);
        setSubType('');
      }
    },
    [setType, setSubType],
  );

  const handleSubTypeClick = useCallback(
    ({ currentTarget }: React.MouseEvent<HTMLSpanElement>) => {
      const { label } = currentTarget.dataset;

      if (label) {
        setSubType(label);
      }
    },
    [setSubType],
  );

  const checkValid = useCallback(
    (_, data) => {
      if (!currentSubType) {
        return false;
      }

      const { anyof, required } = initRule || currentSubType;

      const isValid =
        (!anyof || anyof.some(x => !!data[x])) && (!required || required.every(x => !!data[x]));

      return isValid;
    },
    [currentSubType, initRule],
  );

  useEffect(() => {
    trigger();
  }, [subType, trigger]);

  const handleCreate = useCallback(
    async data => {
      let description = `${currentSubType?.label || ''}${data.extra ? `: ${data.extra}` : ''}`;

      if (currentSubType?.label && appendDescription?.[currentSubType.label]) {
        description += appendDescription[currentSubType.label];
      }

      const request: any = { description };

      if (data.urls) {
        request.url_list = data.urls.split(/\s+|,|，/).filter((x: string) => !!x);
      }

      if (data.addrs) {
        request.addr_list = data.addrs
          .trim()
          .toLowerCase()
          .split(/\s+|,|，/)
          .filter((x: string) => !!x);
      }

      if (data.issueAddr) {
        request.issue_addr = data.issueAddr.trim().toLowerCase();
      }

      try {
        const isExist = await checkProposalDuplicate(request);

        setDuplicate(isExist.is_exists ? isExist.proposal_id : '');

        if (isExist.is_exists) {
          return;
        }
        const similarProposals = await checkProposalSimilar({
          url_list: request.url_list,
          addr_list: request.addr_list,
        });
        if (similarProposals.length && !forceCreateSimilarProposals) {
          setSimilarProposals(similarProposals);
          return;
        }

        const { proposal } = await createProposal(request);
        if (shareOnStream) {
          try {
            await createStream({
              content: '',
              type: 'data_proposal',
              proposal_id: Number(proposal.id),
            });
          } catch (error: any) {
            // 创建stream可能会有限制，创建失败不影响流程
            history.push(`/proposal/${proposal.id}`);
            return;
          }
        }
        history.push(`/proposal/${proposal.id}`);
      } catch (error: any) {
        message.error(errorToString(error) || t('CreatePropsalModal.failed'));
      }
    },
    [
      currentSubType?.label,
      appendDescription,
      checkProposalDuplicate,
      checkProposalSimilar,
      forceCreateSimilarProposals,
      createProposal,
      shareOnStream,
      history,
      createStream,
      t,
    ],
  );

  return (
    <CommonModal turnOff={_requestClose} containerClassName={style.modal} size="medium">
      <div className={style.title}>
        {similarProposals?.length
          ? t('CreatePropsalModal.similar')
          : t('CreatePropsalModal.createTIp')}
      </div>
      {similarProposals?.length ? (
        <div className={style.similarProposalsBody}>
          <div className={style.subTitle}>{t('CreatePropsalModal.zktp84')}</div>
          <div className={style.similarProposalsList}>
            {similarProposals?.map((item, idx) => (
              <ProposalListItem
                data={item}
                customClass={style.similarProposalsListItem}
                isLogged={!!userId}
                onUpdate={onUpdate}
                border={idx !== similarProposals.length - 1}
                urlTarget="_blank"
                source="create_page"
                key={item.id}
              />
            ))}
          </div>
          <div
            className={style.forceCreateSimilarProposals}
            onClick={() => setForceCreateSimilarProposals(!forceCreateSimilarProposals)}>
            <Checkbox className={style.checkbox} checked={forceCreateSimilarProposals} />
            {t('CreatePropsalModal.1i1hik6')}
          </div>
          <div>
            <Button
              className={cx(style.submit, style.submitSimilar)}
              type="primary"
              onClick={handleSubmit(handleCreate)}
              disabled={!forceCreateSimilarProposals}
              loading={isLoading || createStreamLoading}>
              {t('CreatePropsalModal.pswf2y')}
            </Button>
            <Button
              className={cx(style.submit, style.cancelButton)}
              type="default"
              onClick={_requestClose}>
              {t('CreatePropsalModal.x9d2fu')}
            </Button>
          </div>
        </div>
      ) : (
        <div className={style.body}>
          <div className={style.subTitle}>{t('CreatePropsalModal.eux6xz')}</div>
          <div className={style.types}>
            {PROPOSAL_TYPES.map(x => (
              <div className={style.typeTag} key={x.label}>
                <Tag
                  rounded
                  data-label={x.label}
                  color="lightgray"
                  onClick={handleTypeClick}
                  className={cx(style.tag, type === x.label && style.isSelected)}>
                  {x.label}
                </Tag>
                {type === x.label ? <div className={style.typeTriangle} /> : null}
              </div>
            ))}
          </div>
          <div className={style.subTypes}>
            {subTypes?.map(x => (
              <Tag
                key={x.label}
                rounded
                color="lightgray"
                data-label={x.label}
                onClick={handleSubTypeClick}
                className={cx(style.tag, subType === x.label && style.isSelected)}>
                {x.label}
              </Tag>
            ))}
          </div>
          <div className={style.fieldTitle}>{currentType?.urlTitle}</div>
          <Controller
            name="urls"
            control={control}
            rules={{
              validate: checkValid,
            }}
            render={({ field }) => (
              <Input
                className={style.fieldInput}
                placeholder={t('CreatePropsalModal.officialLinks')}
                maxLength={1000}
                // error={!!errors.urls}
                {...field}
              />
            )}
          />
          <div className={style.fieldTitle}>
            {currentSubType?.addrTitle || currentType?.addrTitle}
          </div>
          <Controller
            name="addrs"
            control={control}
            rules={{
              validate: checkValid,
            }}
            render={({ field }) => (
              <Textarea
                className={style.fieldInput}
                placeholder={t('CreatePropsalModal.addrPlace')}
                rows={3}
                maxLength={2000}
                // error={!!errors.addrs}
                {...field}
              />
            )}
          />
          <div className={style.fieldTitle}>
            {currentSubType?.extraTitle || currentType?.extraTitle}
          </div>
          <Controller
            name="extra"
            control={control}
            rules={{
              validate: checkValid,
            }}
            render={({ field }) => (
              <Input
                className={style.fieldInput}
                placeholder={t('CreatePropsalModal.extraHolder')}
                maxLength={1000}
                // error={!!errors.extra}
                {...field}
              />
            )}
          />
          <div className={style.fieldTitle}>{t('CreatePropsalModal.bj6vvt')}</div>
          <Controller
            name="issueAddr"
            control={control}
            rules={{
              validate: checkValid,
            }}
            render={({ field }) => (
              <Input
                disabled
                className={style.fieldInput}
                placeholder={t('CreatePropsalModal.addressPlace')}
                maxLength={1000}
                {...field}
              />
            )}
          />
          {/* 判断是否同时发布一个proposal类型的post */}
          <Checkbox
            className={style.checkboxField}
            type="form"
            checked={shareOnStream}
            onChange={val => setShareOnStream(val?.target?.checked)}>
            {t('CreatePropsalModal.shareOnStream')}
          </Checkbox>
          {duplicate ? (
            <div className={style.duplicate}>
              {t('CreatePropsalModal.atfk5k')}{' '}
              <a
                className={style.duplicateLink}
                target="_blank"
                rel="noreferrer"
                href={`https://debank.com/proposal/${duplicate}`}>
                https://debank.com/proposal/{duplicate}
              </a>
            </div>
          ) : null}
          <HelperTooltip title={isValid ? null : t('CreatePropsalModal.warn')}>
            <Button
              className={style.submit}
              type="primary"
              onClick={handleSubmit(handleCreate)}
              disabled={!isValid}
              loading={isLoading || isCheckDuplicateLoading || isCheckSimilarLoading}>
              {t('CreatePropsalModal.154vgag')}
            </Button>
          </HelperTooltip>
        </div>
      )}
    </CommonModal>
  );
};

export const openProposalCreateModal = wrapModalPromise<CreatePropsalModalProps>(
  CreatePropsalModal,
  {
    wrapInCommonModal: false,
  },
);
